import { ReactElement, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import SessionForm from './SessionForm';
import TabMenu from '../../../components/TabMenu';
import { HiOutlineChartBarSquare, HiOutlineCalculator, HiOutlineListBullet } from 'react-icons/hi2';
import { SessionAnalysisType } from '../../../types/SessionAnalysisType';
import { useRidersById } from '../../../hooks/useRiders';
import { format, parse, isValid } from 'date-fns';
import { useSessionColor, getSessionColorHex, createColoredFavicon, setFavicon } from '../../../hooks/useSessionColor';

interface SessionDetailsProps {
  sessionId: string;
  session: any;
  isSessionPending: boolean;
  isSessionFetching: boolean;
  isNew: boolean;
  refetchSession: () => Promise<void>;
}

export default function SessionDetails({
  sessionId,
  session,
  isSessionPending,
  isSessionFetching,
  isNew,
  refetchSession,
}: SessionDetailsProps): ReactElement {
  const { data: ridersById } = useRidersById();
  const [sessionColor] = useSessionColor(sessionId);
  const colorHex = getSessionColorHex(sessionColor);

  // Store the original favicon to restore on unmount
  const originalFaviconRef = useRef<string | null>(null);

  // Capture the original favicon on mount
  useEffect(() => {
    const link = document.querySelector('link[rel*=\'icon\']') as HTMLLinkElement | null;
    originalFaviconRef.current = link?.href || '/favicon.ico';
  }, []);

  // Update browser tab title with rider name and date
  useEffect(() => {
    if (session && !isNew) {
      const riderName = ridersById?.get(session.rider_id)?.nickname || 'Unknown Rider';
      let dateStr = '';
      if (session.date) {
        const parsedDate = parse(session.date, 'yyyy-MM-dd', new Date());
        if (isValid(parsedDate)) {
          dateStr = format(parsedDate, 'MMM d, yyyy');
        }
      }
      document.title = dateStr ? `${riderName} - ${dateStr}` : riderName;
    } else if (isNew) {
      document.title = 'New Session';
    }

    // Cleanup: restore default title when component unmounts
    return () => {
      document.title = 'Cirrus';
    };
  }, [session, ridersById, isNew, sessionColor]);

  // Update favicon based on session color
  useEffect(() => {
    const coloredFavicon = createColoredFavicon(sessionColor);
    setFavicon(coloredFavicon, originalFaviconRef.current || undefined);

    // Cleanup: restore original favicon when component unmounts
    return () => {
      setFavicon(null, originalFaviconRef.current || undefined);
    };
  }, [sessionColor]);

  // Check if Tyre Plots should be shown based on analysis_type
  const showTyrePlots =
    session?.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_REFERENCE ||
    session?.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON;

  // Build tabs array conditionally
  const tabs = [
    { path: 'config', label: 'Plan', icon: <HiOutlineListBullet className={'cr-size-7'} /> },
    { path: 'results', label: 'Data Table', icon: <HiOutlineCalculator className={'cr-size-7'} /> },
    { path: 'charts', label: 'Aero Report', icon: <HiOutlineChartBarSquare className={'cr-size-7'} /> },
  ];

  // Add Tyre Report tab only if conditions are met
  if (showTyrePlots) {
    tabs.push({ path: 'tyre-plots', label: 'Tyre Report', icon: <HiOutlineChartBarSquare className={'cr-size-7'} /> });
  }

  return (
    <div className="cr-relative cr-w-full">
      {/* Vertical color stripes on left and right sides */}
      {sessionColor && (
        <>
          <div
            className="cr-fixed cr-left-0 cr-top-0 cr-h-full cr-w-1 cr-z-50"
            style={{ backgroundColor: colorHex }}
          />
          <div
            className="cr-fixed cr-right-0 cr-top-0 cr-h-full cr-w-1 cr-z-50"
            style={{ backgroundColor: colorHex }}
          />
        </>
      )}
      <SessionForm
        sessionId={sessionId}
        session={session}
        isSessionFetching={isSessionFetching}
        isSessionPending={isSessionPending}
        isNew={isNew}
      />
      {session?.configs && session.configs.length > 0 && (
        <>
          <TabMenu tabs={tabs} basePath={`/sessions/${sessionId}`} className="cr-mb-6" />
          <Outlet context={{ sessionId, session, isSessionFetching, isSessionPending, isNew, refetchSession }} />
        </>
      )}
    </div>
  );
}
